import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition } from "../common/models";
import { Card, Typography } from "@mui/material";
import { randomUUID } from "../../../utils";

import Icon from '@mdi/react';
import { mdiAlert } from '@mdi/js';
import { translateReactNode } from "../../../../utils/translateReactNode";
import theme from "../../../../theme";

const TUTORIAL_URLS: Record<string, string> = {
  'en': 'assets/videos/tutorials/Free_Speech_v2.mp4',
  'es-419': 'assets/videos/tutorials/Free_Speech_v2-es-419.mp4'
}

const WarningCard = () => {
  const { t } = useTranslation('generic');

  return (
  <div style={{ margin: '1rem 0', }}>
    <Card style={{
      padding: '1rem',
      color: 'white',
      background: theme.palette.primary.main,
      margin: 'auto',
      maxWidth: '800px'}}>
        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem', justifyContent: 'center', }}>
          <Icon path={mdiAlert} size={10} style={{ maxHeight: '5rem'}}/>
          <Typography variant="body1">
            {t("We'd like to get a recording of your natural speech. Please answer the following questions as though you were having a conversation. We'll record about 30 seconds of your response.")}
          </Typography>
        </div>
      </Card>
    </div>
  );
};

const Definition: AcousticTaskDefinition = {
  name: "Free speech (v2)",
  cohort: 'generic',

  title: "Free speech",
  description: "This section is meant to hear you speak freely by answering an open-ended question.",
  type: 'acoustic-task',
  estimatedCompletionTime: '3:10',
  subTaskDefinitions: [{
    name: "Free speech (v2)-1",
    title: "",
    instructions: `What is your favorite season (winter, fall, summer, or spring) and why? Talk about the food, clothing, and activities you enjoy at that time.`,
    profileName: 'speech',
    stopwatchSeconds: 30
  }, {
    name: "Free speech (v2)-2",
    title: "",
    instructions: `Thinking back over the past 2 weeks, describe your general mood, either positive or negative. What things have contributed to you feeling this way?`,
    profileName: 'speech',
    stopwatchSeconds: 30
  }, {
    name: "Free speech (v2)-3",
    title: "",
    instructions: `Tell us about your favorite book or movie. Why do you like it?`,
    profileName: 'speech',
    stopwatchSeconds: 30
  }]
};
export const FreeSpeechDefinition = Definition;

export const FreeSpeechGeneric =  ({onCompletion}: {onCompletion: () => void}) => {
  const { i18n } = useTranslation();
  const [task, _setTask] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTasks: Definition.subTaskDefinitions.map((definition) => ({
      ...definition,
      id: randomUUID(),
      instructions: translateReactNode(definition.instructions, Definition.cohort),
      title: translateReactNode(definition.title, Definition.cohort) as string
    })),
    tutorialUrl: TUTORIAL_URLS[i18n.language] || TUTORIAL_URLS['en']
  }));

  const handleOnCompletion = () => {
    console.log("Audio task completed", task.title);
    onCompletion();
  }

  return (
    <div>
      <TaskView
        task={task}
        content={<WarningCard/>}
        onCompletion={handleOnCompletion}
      />
    </div>
  );
}
